'use client';

/**
 * useNativeBackButton Hook
 *
 * Handles native back button press from mobile app.
 * When running inside a mobile app WebView, the native back button
 * sends a message that we need to handle.
 */

import { useEffect, useRef } from 'react';
import type { MobileAppProps } from '../types';
import { messageToAppKeys } from '../mobile/mobileMessaging';

interface UseNativeBackButtonOptions {
  /** Mobile app props containing received message and clear function */
  mobileAppProps?: MobileAppProps;
  /** Handler to call when native back button is pressed */
  onBackPress: () => void;
  /** Whether the handler is enabled (e.g., disable during processing) */
  enabled?: boolean;
}

/**
 * Hook to handle native back button press from mobile app
 *
 * @example
 * ```tsx
 * useNativeBackButton({
 *   mobileAppProps,
 *   onBackPress: handleBackClick,
 *   enabled: currentScreen !== SCREENS.PAYMENT_PROCESSING
 * });
 * ```
 */
export const useNativeBackButton = ({
  mobileAppProps,
  onBackPress,
  enabled = true
}: UseNativeBackButtonOptions): void => {
  // Use ref to track if we've already handled this message
  const lastHandledMessageRef = useRef<Record<string, any> | null>(null);

  useEffect(() => {
    if (!enabled || !mobileAppProps) {
      return;
    }

    const { receivedMessageFromApp, clearReceivedMessage } = mobileAppProps;

    // Check if we have a new message to handle
    if (!receivedMessageFromApp) {
      return;
    }

    // Check if this is a native back press action
    const isNativeBackPress = receivedMessageFromApp[messageToAppKeys.action] === messageToAppKeys.nativeBackPressed;

    if (!isNativeBackPress) {
      return;
    }

    // Avoid handling the same message twice
    if (lastHandledMessageRef.current === receivedMessageFromApp) {
      return;
    }

    // Mark as handled
    lastHandledMessageRef.current = receivedMessageFromApp;

    // Clear the message so it doesn't trigger again
    clearReceivedMessage?.();

    // Call the back handler
    onBackPress();
  }, [mobileAppProps, onBackPress, enabled]);
};

export default useNativeBackButton;
